import React from 'react';

const CHANNEL_CONFIG = {
  offline: { label: 'Offline Marketing', color: '#f59e0b', icon: '📦' },
  onlineAds: { label: 'Online Ads', color: '#10b981', icon: '🎯' },
  socialMedia: { label: 'Social Media', color: '#3b82f6', icon: '📱' },
};

const CampaignStatusDashboard = ({ status, isDarkMode, onViewChannel, onDownloadAll, onViewAnalytics }) => {
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';

  const channels = Object.entries(CHANNEL_CONFIG).filter(([key]) => status[key]);

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[90%] md:max-w-[80%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist — Campaign Dashboard
        </p>

        <div className={`px-4 py-4 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className={`text-sm font-bold ${textPrimary}`}>Campaign Status</h4>
              <p className={`text-xs ${textMuted} mt-1`}>
                Strategy approved. {channels.length} channel{channels.length !== 1 ? 's' : ''} active.
              </p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
              Active
            </span>
          </div>

          {/* Channel cards */}
          <div className="space-y-3 mb-4">
            {channels.map(([key, config]) => {
              const chStatus = status[key];
              const color = config.color;
              const progress = chStatus.total > 0 ? (chStatus.approved / chStatus.total) * 100 : 0;
              const isLive = chStatus.status === 'live';
              const isScheduled = chStatus.status === 'scheduled';
              const isReady = chStatus.status === 'ready';

              return (
                <div
                  key={key}
                  onClick={() => onViewChannel(key)}
                  className={`rounded-xl border ${borderColor} ${cardBg} p-3 cursor-pointer transition-all hover:shadow-sm`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm`}
                        style={{ backgroundColor: `${color}22`, color }}
                      >
                        {config.icon}
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${textPrimary}`}>{config.label}</p>
                        <p className={`text-xs ${textMuted}`}>
                          {chStatus.approved}/{chStatus.total} approved
                          {chStatus.published !== undefined && ` • ${chStatus.published} published`}
                          {chStatus.scheduled !== undefined && ` • ${chStatus.scheduled} scheduled`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isLive && chStatus.roas && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium bg-purple-500/20 text-purple-400`}>
                          ROAS {chStatus.roas}x
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isLive
                        ? 'bg-green-500/20 text-green-500'
                        : isScheduled
                          ? 'bg-blue-500/20 text-blue-400'
                          : isReady
                            ? 'bg-cyan-500/20 text-cyan-500'
                            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isLive ? '🟢 Live' : isScheduled ? '📅 Scheduled' : isReady ? '✓ Ready' : 'Draft'}
                      </span>
                      <svg className={`w-4 h-4 ${textSecondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {chStatus.total > 0 && (
                    <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${progress}%`,
                          background: `linear-gradient(90deg, ${color}, ${color}dd)`,
                        }}
                      />
                    </div>
                  )}

                  {/* Extra metrics */}
                  <div className="flex items-center gap-3 mt-2">
                    {isLive && chStatus.spend && (
                      <span className={`text-xs ${textMuted}`}>Spend: ₹{chStatus.spend}</span>
                    )}
                    {isLive && chStatus.ctr && (
                      <span className={`text-xs ${textMuted}`}>CTR: {chStatus.ctr}%</span>
                    )}
                    {isScheduled && chStatus.nextPost && (
                      <span className={`text-xs ${textMuted}`}>Next: {chStatus.nextPost}</span>
                    )}
                    {isReady && chStatus.readyCount && (
                      <span className={`text-xs ${textMuted}`}>{chStatus.readyCount} ready to download</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={onViewAnalytics}
              className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] hover:shadow-lg hover:brightness-110 text-white`}
              style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              View Analytics
            </button>
            <button
              onClick={onDownloadAll}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] flex items-center gap-2 ${isDarkMode
                ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export All
            </button>
          </div>

          <div className="flex items-center mt-2 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignStatusDashboard;
